// #kinship-quiz-unification — Kinship Quiz V2.
//
// New quiz built on the production kinship engine (server-side via
// POST /api/public/kinship-labels). Lives alongside the legacy
// RelationQuiz.tsx (which still ships on /quiz and /play); after
// this dispatch lands and Ramesh validates V2 end-to-end, a
// follow-up dispatch will cut /quiz over to V2 and delete the
// legacy parallel-engine files.
//
// Boundary rule: V2 must NEVER import from apps/web/src/lib/
// kinshipAlgebra.ts or kinshipLang.ts. Engine queries flow only
// through useKinshipLabels (HTTP boundary).
//
// Commit 3 status: levels 1-4 playable. Levels 5+6 land in commit
// 4 with descent-then-ascent + telemetry.

import React, { useEffect, useMemo, useState } from 'react'
import { type KinshipLang } from '../api/hooks/useKinshipLabels'
import { useChainPool, type PoolEntry } from '../api/hooks/useChainPool'
import { chainKey } from '../lib/quizChainPool'
import {
  pickEntry, pickEgoGender, buildPuzzleFromPoolEntry, buildPuzzleFromPoolEntryL7,
  requiredEgoForChain,
  unionRecentNames, pushPuzzleNames, RECENT_PUZZLE_WINDOW,
  type QuizPuzzle, type AnswerOption,
} from '../lib/quizPuzzle'
import type { CharacterGender, PuzzleCharacter } from '../lib/quizNames'
import { apiClient } from '../api/client'

const NAVY = '#003153'
const GOLD = '#E8B800'
const GREEN = '#2E7D32'
const RED   = '#C62828'

const PUZZLES_PER_LEVEL  = 10
const PASS_THRESHOLD     = 7
const RECENT_CHAIN_WINDOW = 5    // last N chain keys, for chain dedup
// #quiz-l7-ui — bumped to include L7 (inverse-relations puzzles).
// L7 ships as a selectable level in the header; perspective-flipped
// rendering is gated by puzzle.l7 metadata so L1-L6 stays unchanged.
const SUPPORTED_LEVELS   = [1, 2, 3, 4, 5, 6, 7] as const
const PLANNED_LEVELS     = [1, 2, 3, 4, 5, 6, 7] as const

// #kinship-quiz-level-6-named-terms — Level 6 re-enabled after the
// engine fixes from 635e074 (ascent-then-descent + parallel-cousin
// sibling propagation) and f901ebd (WIFE/HUSBAND atoms). L6 is now
// the "named indirect terms" level: each puzzle displays a 2-arrow
// chain (named term + direct atom) that the player composes from
// the L3-introduced named relations (Mama, Atta, Bava, Vadina,
// Menalludu, Menakodalu) plus the spouse/affinal terms (Bharya,
// Bharta, Kodalu, Alludu) appended with a single direct atom.
export const VISIBLE_LEVELS = [1, 2, 3, 4, 5, 6, 7] as const

type SupportedLevel = (typeof SUPPORTED_LEVELS)[number]

// #kinship-quiz-unification commit 4 — per-session anonymous UUID
// stamped on every telemetry event. RFC 4122 v4 via crypto.randomUUID,
// generated once at component mount; not persisted, not tied to any
// user identifier. Fallback to a Math.random-based 36-char id on
// browsers that lack crypto.randomUUID (rare in 2026 but defensive).
function generateSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
  } catch { /* fall through */ }
  return Array.from({ length: 36 }, () =>
    'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]
  ).join('')
}

interface TelemetryEvent {
  level: number
  chain: string[]
  egoGender: CharacterGender
  correctAnswer: string
  playerAnswer: string
  isCorrect: boolean
  sessionId: string
}

function postTelemetry(event: TelemetryEvent): void {
  // Fire-and-forget POST. Failures are logged but never surface to
  // the player — telemetry is best-effort, not a UX gate.
  apiClient.post('/api/public/quiz-telemetry', event).catch((err) => {
    console.warn('quizTelemetry POST failed', err)
  })
}
type LevelOutcome = 'in-progress' | 'passed' | 'failed'

// #kinship-quiz-defer-l6-and-back-nav — per-puzzle record retained
// in session history so players can navigate Back to previously
// rendered puzzles and re-read the question + their answer + the
// correct answer. `picked` is null until the player answers; once
// set, the entry is immutable (handlePick's pickedOption early-
// return prevents overwrites on re-visit).
export interface HistoryEntry {
  puzzle: QuizPuzzle
  picked: AnswerOption | null
}

interface SessionState {
  level: number
  puzzleIndex: number
  recentChainKeys: Set<string>
  // #kinship-quiz-pickname-exhausted: recent names tracked as a
  // list of per-puzzle name sets, bounded by RECENT_PUZZLE_WINDOW
  // (3 puzzles). The exclude set passed to buildPuzzle is the
  // union — at most ~15 names with 5-char chains, comfortably
  // under each gender pool's 10-entry capacity.
  recentPuzzleNames: Set<string>[]
  outcome: LevelOutcome
  // History of rendered puzzles + the player's selection per puzzle.
  // puzzleIndex addresses into this array; values past
  // history.length-1 trigger new-puzzle generation. Going Back is a
  // pure index decrement; Next is a pure index increment that
  // replays existing history before generating a fresh puzzle at
  // the tail. Cleared on level switch.
  history: HistoryEntry[]
}

const initialSession = (level: number): SessionState => ({
  level,
  puzzleIndex: 0,
  recentChainKeys: new Set(),
  recentPuzzleNames: [],
  outcome: 'in-progress',
  history: [],
})

/** Pure helper: is the Back button enabled? */
export function canGoBack(puzzleIndex: number): boolean {
  return puzzleIndex > 0
}

/** Pure helper: derive { right, total } from a history slice up
 *  to and including the current puzzleIndex. */
export function computeScore(history: ReadonlyArray<HistoryEntry>, puzzleIndex: number): {
  right: number
  total: number
} {
  const cap = Math.min(history.length, puzzleIndex + 1)
  const slice = history.slice(0, cap)
  return {
    right: slice.filter((h) => h.picked?.isCorrect === true).length,
    total: slice.filter((h) => h.picked !== null).length,
  }
}

export function RelationQuizV2(): React.JSX.Element {
  // #kinship-quiz-chainpool-server: chain pools fetched from the
  // backend's precomputed endpoint, one GET per level. 6 GETs total
  // at mount, all 24h-cacheable. Replaces the previous client-side
  // L6 enumeration + chunked kinshipLabels POSTs that tripped the
  // 30 req/min rate limiter.
  //
  // Each PoolEntry already carries ego-male + ego-female labels and
  // precomputed single-atom mutation labels — the client never
  // needs to call kinshipLabels for puzzle generation.
  const pool1 = useChainPool(1)
  const pool2 = useChainPool(2)
  const pool3 = useChainPool(3)
  const pool4 = useChainPool(4)
  const pool5 = useChainPool(5)
  const pool6 = useChainPool(6)
  const pool7 = useChainPool(7)

  const isLoading =
    pool1.isLoading || pool2.isLoading || pool3.isLoading ||
    pool4.isLoading || pool5.isLoading || pool6.isLoading ||
    pool7.isLoading

  const pools = useMemo(
    () => ({
      1: pool1.entries, 2: pool2.entries, 3: pool3.entries,
      4: pool4.entries, 5: pool5.entries, 6: pool6.entries,
      7: pool7.entries,
    }) satisfies Record<SupportedLevel, PoolEntry[]>,
    [
      pool1.entries, pool2.entries, pool3.entries,
      pool4.entries, pool5.entries, pool6.entries,
      pool7.entries,
    ],
  )

  // Per-session anonymous UUID for telemetry. Generated once at
  // first render via lazy useState init.
  const [sessionId] = useState<string>(() => generateSessionId())

  // #kinship-quiz-question-direction: lang is stateful so a future
  // language picker can toggle. Defaults to Telugu. The server-side
  // pool currently returns Telugu only; English support would need
  // either an additional pool endpoint or a per-language pool param.
  const [lang, _setLang] = useState<KinshipLang>('te')
  // lang is reserved for future picker wiring; reference it so the
  // value isn't dead.
  void lang

  const [session, setSession] = useState<SessionState>(() => initialSession(1))

  // Currently-displayed puzzle + picked option. Derived from
  // session.history[session.puzzleIndex] via the effect below.
  // Kept as separate state (rather than derived inline) so the
  // OptionsGrid / Feedback subcomponents don't have to know about
  // the history shape.
  const [currentPuzzle, setCurrentPuzzle] = useState<QuizPuzzle | null>(null)
  const [pickedOption, setPickedOption]   = useState<AnswerOption | null>(null)

  useEffect(() => {
    if (isLoading) return
    if (session.outcome !== 'in-progress') return
    const lvl = session.level
    if (!isSupportedLevel(lvl)) return

    const pool = pools[lvl]
    if (!pool || pool.length === 0) return

    // #kinship-quiz-distractor-fallback — adjacent-level pools in
    // priority order. L1 → [L2]; L2-L4 → [L_{N-1}, L_{N+1}];
    // L5 → [L4] (L6 deferred per #kinship-quiz-defer-l6, never
    // surfaced as adjacent); L6 itself → [L5] if it ever runs.
    // Filtered to non-empty pools so empty/uninitialised levels
    // don't pollute the fallback chain.
    const adjacentPools = buildAdjacentPools(lvl, pools)

    // #kinship-quiz-defer-l6-and-back-nav — if this index is
    // already in history, replay it (Back navigation OR Next
    // returning to a previously-built puzzle). Picked option is
    // restored so the player sees Feedback as they left it.
    if (session.puzzleIndex < session.history.length) {
      const replay = session.history[session.puzzleIndex]
      setCurrentPuzzle(replay.puzzle)
      setPickedOption(replay.picked)
      return
    }

    // Otherwise: build a new puzzle and append to history. The
    // setSession below triggers a re-render; the next pass falls
    // into the replay branch above.
    //
    // #kinship-quiz-pool-self-filter-and-names — Fix 2 retry loop.
    // buildPuzzleFromPoolEntry throws defensively if a pool entry's
    // correct-label resolves to self. On throw, pick a different
    // entry. 5 attempts is enough — bad entries are rare and the
    // pool has 4-50 entries per level.
    const rng = Math.random
    let puzzle: QuizPuzzle | null = null
    const triedKeys = new Set<string>(session.recentChainKeys)
    for (let attempt = 0; attempt < 5 && !puzzle; attempt++) {
      try {
        const entry     = pickEntry(pool, triedKeys, rng)
        triedKeys.add(chainKey(entry.atoms))
        // #quiz-l7-ui — L7 uses a dedicated builder (perspective-
        // flipped; B is ego; A is the question target). No
        // requiredEgoForChain check — L7 derives B's gender from
        // the chain's LAST atom, not the first.
        if (lvl === 7) {
          puzzle = buildPuzzleFromPoolEntryL7({
            entry,
            pool,
            recentNames: unionRecentNames(session.recentPuzzleNames),
            rng,
          })
          continue
        }
        // #kinship-quiz-name-gender-and-viyyankudu-synonyms — Fix 1
        //
        // Random ego picks are overridden when the chain dictates a
        // specific ego gender. Wife-first chains (Bharya entries)
        // require male ego; Husband-first chains (Bharta entries)
        // require female ego. Without this, an L6 Bharya entry
        // [Wife, Daughter] could be picked with female ego, yielding
        // a nonsensical "Sunita has a Bharya" puzzle (the symptom
        // from Ramesh's staging smoke).
        //
        // For L6 entries we read the per-ego expansion via the l6
        // metadata; for L1-L5 we read entry.atoms directly. Either
        // way, the FIRST atom drives the constraint.
        const firstAtom = entry.l6
          ? entry.l6.atomsByEgo.male[0]   // both ego paths share the same chain-start atom
          : entry.atoms[0]
        const forcedEgo = requiredEgoForChain([firstAtom, ...(entry.atoms.slice(1))])
        const egoGender = forcedEgo ?? pickEgoGender(rng)
        puzzle = buildPuzzleFromPoolEntry({
          level: lvl,
          egoGender,
          entry,
          pool,
          adjacentPools,
          recentNames: unionRecentNames(session.recentPuzzleNames),
          rng,
        })
      } catch (err) {
        console.warn(
          `RelationQuizV2: attempt ${attempt + 1}/5 failed; skipping entry`, err,
        )
      }
    }
    if (puzzle) {
      const builtPuzzle = puzzle
      setSession((s) => ({
        ...s,
        history: [...s.history, { puzzle: builtPuzzle, picked: null }],
      }))
      // The setSession above schedules a re-render; the next
      // effect pass will hit the replay branch and set
      // currentPuzzle/pickedOption from history. Setting them
      // directly here too avoids a single-render flash where
      // currentPuzzle still points at the prior puzzle.
      setCurrentPuzzle(builtPuzzle)
      setPickedOption(null)
    } else {
      console.error(
        `RelationQuizV2: exhausted 5 attempts to build a puzzle for level ${lvl}; ` +
        `pool may be entirely corrupt`,
      )
    }
  }, [session.level, session.puzzleIndex, session.outcome, session.history, isLoading, pools, session.recentChainKeys, session.recentPuzzleNames])

  function handlePick(opt: AnswerOption): void {
    if (pickedOption) return  // re-visiting a previously-answered puzzle: no-op
    setPickedOption(opt)
    setSession((s) => {
      // Record the pick in history so Back navigation can replay it.
      const updatedHistory = [...s.history]
      const current = updatedHistory[s.puzzleIndex]
      if (current) {
        updatedHistory[s.puzzleIndex] = { ...current, picked: opt }
      }
      return { ...s, history: updatedHistory }
    })
    // Fire telemetry once per answer pick. Failures swallowed.
    if (currentPuzzle) {
      postTelemetry({
        level:         currentPuzzle.level,
        chain:         currentPuzzle.chain,
        egoGender:     currentPuzzle.egoGender,
        correctAnswer: currentPuzzle.correct.term,
        playerAnswer:  opt.term,
        isCorrect:     opt.isCorrect,
        sessionId,
      })
    }
  }

  function handleAdvance(): void {
    if (!currentPuzzle) return
    const nextIndex = session.puzzleIndex + 1
    // Only update the recents windows when crossing INTO new
    // territory (puzzleIndex past the previous high-water mark).
    // Re-visiting via Back→Next shouldn't re-mark a chain as
    // recent — it's already in the history surface, and the
    // recents window would double-count it.
    const crossingIntoNew = nextIndex > session.history.length - 1
    const nextChainKeys = crossingIntoNew
      ? trimWindow(
          new Set([...session.recentChainKeys, chainKey(currentPuzzle.chain)]),
          RECENT_CHAIN_WINDOW,
        )
      : session.recentChainKeys
    const nextPuzzleNames = crossingIntoNew
      ? pushPuzzleNames(
          session.recentPuzzleNames,
          [currentPuzzle.egoName, ...currentPuzzle.characters.map((c) => c.name)],
          RECENT_PUZZLE_WINDOW,
        )
      : session.recentPuzzleNames
    if (nextIndex >= PUZZLES_PER_LEVEL) {
      const { right } = computeScore(session.history, session.puzzleIndex)
      const passed = right >= PASS_THRESHOLD
      setSession((s) => ({
        ...s, puzzleIndex: nextIndex,
        recentChainKeys: nextChainKeys, recentPuzzleNames: nextPuzzleNames,
        outcome: passed ? 'passed' : 'failed',
      }))
    } else {
      setSession((s) => ({
        ...s, puzzleIndex: nextIndex,
        recentChainKeys: nextChainKeys, recentPuzzleNames: nextPuzzleNames,
      }))
    }
  }

  // #kinship-quiz-defer-l6-and-back-nav — step back one puzzle.
  // No-op when puzzleIndex === 0 (no previous to go to). The
  // effect handles replay automatically when puzzleIndex changes.
  function handleBack(): void {
    if (!canGoBack(session.puzzleIndex)) return
    setSession((s) => ({ ...s, puzzleIndex: s.puzzleIndex - 1 }))
  }

  function handleNextLevel(): void {
    setSession(initialSession(session.level + 1))
    setCurrentPuzzle(null)
    setPickedOption(null)
  }

  function handleReplayLevel(): void {
    setSession(initialSession(session.level))
    setCurrentPuzzle(null)
    setPickedOption(null)
  }

  // #kinship-quiz-level-selector — direct level switch via the
  // header tab row. Resets all per-session state (score, recent
  // chains, recent names, history, current puzzle). Per dispatch:
  // no confirmation, no warning, no progression gate.
  function handleSelectLevel(n: number): void {
    setSession(initialSession(n))
    setCurrentPuzzle(null)
    setPickedOption(null)
  }

  // Derived score — counts answered puzzles in history up through
  // the current puzzleIndex. Going Back decreases displayed score;
  // going Next forward restores it. Stable across re-renders.
  const { right: rightSoFar, total: totalSoFar } = useMemo(
    () => computeScore(session.history, session.puzzleIndex),
    [session.history, session.puzzleIndex],
  )

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF5', padding: '32px 16px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <Header
          level={session.level}
          rightSoFar={rightSoFar}
          totalSoFar={totalSoFar}
          onSelectLevel={handleSelectLevel}
          lang={lang}
        />

        {isLoading && <LoadingCard />}

        {!isLoading && session.outcome === 'in-progress' && isSupportedLevel(session.level) && currentPuzzle && (
          <PuzzleCard
            puzzle={currentPuzzle}
            picked={pickedOption}
            onPick={handlePick}
            onAdvance={handleAdvance}
            onBack={handleBack}
            canBack={canGoBack(session.puzzleIndex)}
            lang={lang}
          />
        )}

        {!isLoading && session.outcome === 'in-progress' && !isSupportedLevel(session.level) && (
          <ComingSoonCard level={session.level} onBack={() => setSession(initialSession(1))} />
        )}

        {!isLoading && session.outcome !== 'in-progress' && (
          <LevelCompleteCard
            level={session.level}
            rightSoFar={session.history.filter((h) => h.picked?.isCorrect).length}
            outcome={session.outcome}
            canAdvance={(PLANNED_LEVELS as readonly number[]).includes(session.level + 1)}
            onReplay={handleReplayLevel}
            onAdvance={handleNextLevel}
            lang={lang}
          />
        )}
      </div>
    </div>
  )
}

function isSupportedLevel(n: number): n is SupportedLevel {
  return (SUPPORTED_LEVELS as readonly number[]).includes(n)
}

// #kinship-quiz-distractor-fallback — adjacent-level pool order
// for the Tier 3 fallback. Returns N-1 first (smaller chains,
// more conceptually distinct), then N+1. L6 is excluded from
// any adjacency list per #kinship-quiz-defer-l6 (deferred from
// active levels; don't surface its chains via fallback either).
//
// Empty pools (e.g. before fetch completes) are filtered out
// so adjacent fallback only consults populated levels.
export function buildAdjacentPools(
  level: number,
  pools: Partial<Record<SupportedLevel, PoolEntry[]>>,
): PoolEntry[][] {
  // #quiz-l7-ui — L7 has its own distractor strategy (pool-other-
  // chain WITHIN the 20-entry L7 pool, using B-gender-keyed labels).
  // Don't surface L1-L6 forward labels as L7 distractors — they'd
  // be perspective-mismatched (forward terms vs inverse answer).
  // Same the other way: L7 entries shouldn't leak into L1-L6
  // adjacency.
  if (level === 7) return []
  const out: PoolEntry[][] = []
  const prev = level - 1
  const next = level + 1
  if (prev >= 1 && prev <= 6) {
    const p = pools[prev as SupportedLevel]
    if (p && p.length > 0) out.push(p)
  }
  if (next >= 1 && next <= 6 && next !== 6) {
    const p = pools[next as SupportedLevel]
    if (p && p.length > 0) out.push(p)
  }
  return out
}

// ── Subcomponents ────────────────────────────────────────────────

// #kinship-quiz-level-selector — Header now renders a row of 6
// level buttons in place of the prior single chip. Tapping any
// button immediately resets the session to that level (per
// dispatch: no confirmation, no progression gate). The quiz is a
// top-of-funnel enthusiasm-builder, not a strict assessment.
function Header({ level, rightSoFar, totalSoFar, onSelectLevel, lang }: {
  level: number
  rightSoFar: number
  totalSoFar: number
  onSelectLevel: (n: number) => void
  lang: KinshipLang
}): React.JSX.Element {
  return (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ color: GOLD, letterSpacing: 3, fontSize: 11, fontWeight: 600 }}>
        JNATI · KINSHIP CHALLENGE · v2
      </div>
      <h2 style={{ color: NAVY, fontSize: 24, fontWeight: 700, margin: '6px 0 0' }}>
        బంధుత్వం ఏమిటి?
      </h2>
      <LevelSelector activeLevel={level} onSelectLevel={onSelectLevel} lang={lang} />
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, fontSize: 13 }}>
        <span style={chipStyle}>స్కోర్: {rightSoFar} / {totalSoFar}</span>
      </div>
    </div>
  )
}

export function levelButtonLabel(level: number, lang: KinshipLang): string {
  if (lang === 'en') return `Level ${level}`
  return `స్థాయి ${level}`
}

function LevelSelector({ activeLevel, onSelectLevel, lang }: {
  activeLevel: number
  onSelectLevel: (n: number) => void
  lang: KinshipLang
}): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={lang === 'en' ? 'Quiz level' : 'క్విజ్ స్థాయి'}
      style={{
        display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
        gap: 8, marginTop: 14,
      }}
    >
      {VISIBLE_LEVELS.map((n) => {
        const isActive = n === activeLevel
        return (
          <button
            key={n}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelectLevel(n)}
            style={isActive ? levelButtonActiveStyle : levelButtonInactiveStyle}
          >
            {levelButtonLabel(n, lang)}
          </button>
        )
      })}
    </div>
  )
}

function LoadingCard(): React.JSX.Element {
  return (
    <div style={cardStyle}>
      <p style={{ color: '#666', fontSize: 14, textAlign: 'center', margin: 0 }}>
        Loading kinship engine…
      </p>
    </div>
  )
}

function PuzzleCard({ puzzle, picked, onPick, onAdvance, onBack, canBack, lang }: {
  puzzle: QuizPuzzle
  picked: AnswerOption | null
  onPick: (o: AnswerOption) => void
  onAdvance: () => void
  onBack: () => void
  canBack: boolean
  lang: KinshipLang
}): React.JSX.Element {
  // #quiz-l7-ui — L7 puzzles invert the perspective: ego is B (yellow,
  // at chain END); A is the question target rendered at chain START.
  // QuestionLine + ChainVisualization branch on puzzle.l7 to render
  // accordingly.
  return (
    <div style={cardStyle}>
      {puzzle.l7 ? (
        <>
          <QuestionLineL7 egoName={puzzle.egoName} aName={puzzle.l7.aCharacter.name} lang={lang} />
          <ChainVisualizationL7 puzzle={puzzle} lang={lang} />
        </>
      ) : (
        <>
          <QuestionLine egoName={puzzle.egoName} characters={puzzle.characters} lang={lang} />
          <ChainVisualization egoName={puzzle.egoName} egoGender={puzzle.egoGender} characters={puzzle.characters} lang={lang} visualSteps={puzzle.visualSteps} />
        </>
      )}
      <OptionsGrid options={puzzle.options} picked={picked} onPick={onPick} />
      {picked && (
        <Feedback
          picked={picked}
          correct={puzzle.correct.term}
          onAdvance={onAdvance}
          onBack={onBack}
          canBack={canBack}
        />
      )}
      <InlineSignupCta lang={lang} />
    </div>
  )
}

// #quiz-l7-ui — L7 question line. Asks about A from B's perspective.
// Visual format mirrors L1-L6's QuestionLine: "egoNameకు otherName
// ఏమవుతారు?" — but for L7, the ego IS B (the yellow ego), and the
// "other" being asked about is A (rendered as the first non-yellow
// avatar). Text shape identical to L1-L6 — only the egoName/aName
// arguments differ in source (B's name as ego, A's name as the
// alter).
function QuestionLineL7({ egoName, aName, lang }: {
  egoName: string
  aName: string
  lang: KinshipLang
}): React.JSX.Element {
  return (
    <p style={{ color: NAVY, fontSize: 16, fontWeight: 600, textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
      {buildQuestionText(egoName, aName, lang)}
    </p>
  )
}

// #quiz-l7-ui — L7 chain visualization. Renders B(yellow ego) on
// LEFT, then walks the forward chain rightward to A(rendered as the
// alter). This matches L1-L6's visual convention (yellow ego always
// on the left), with the chain meaning inverted: instead of "ego's
// father is X", L7 reads as "from ego(B)'s perspective walking
// forward, what is A at the chain's far end?".
//
// For atom-less ITHARA_BANDUVU (puzzle.chain empty): renders B(yellow)
// → A directly with no atom arrows, anchored by an empty-chain hint.
function ChainVisualizationL7({ puzzle, lang }: {
  puzzle: QuizPuzzle
  lang: KinshipLang
}): React.JSX.Element {
  if (!puzzle.l7) {
    throw new Error('ChainVisualizationL7: puzzle missing l7 metadata')
  }
  const { aCharacter } = puzzle.l7
  // #quiz-l7-perspective-inversion Bug-2 — L7 visual flips L6's
  // ego-at-source convention: A is at the LEFT (chain source),
  // forward atom arrows walk rightward through intermediates, and
  // B (the yellow ego, perspective-holder) lands at the RIGHT
  // (chain target). Reads as "A's atom1 is intermediate, ..., is B".
  // Pre-fix put B-yellow on the LEFT with arrows pointing
  // rightward — visually claimed "B's father is A" which is
  // geometrically backwards for any chain whose last atom doesn't
  // match A's gender (e.g. Father atom with female A).
  if (puzzle.chain.length === 0) {
    // Symmetric ITHARA_BANDUVU (no chain): render A → ? → B(yellow)
    // so even the no-atom case follows the "yellow ego on right"
    // convention. Question text reads "Bకు A ఏమవుతారు?" so the
    // yellow avatar (B) is rightmost as the perspective-holder.
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        <Avatar name={aCharacter.name} gender={aCharacter.gender} />
        <ArrowWithLabel label='?' />
        <Avatar name={puzzle.egoName} gender={puzzle.egoGender} isEgo />
      </div>
    )
  }
  // Atom-based: A on LEFT, walk forward through intermediates,
  // B(yellow) at the RIGHT end of the chain. characters[N-1] IS B
  // (per buildPuzzleFromPoolEntryL7 — last character = ego). So
  // render aCharacter first, then characters[0..N-2] interleaved
  // with chain[0..N-2] atom arrows, then chain[N-1] atom arrow,
  // then the ego (B) avatar as yellow.
  const intermediates = puzzle.characters.slice(0, -1)
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 20 }}>
      <Avatar name={aCharacter.name} gender={aCharacter.gender} />
      {intermediates.map((ch, i) => (
        <React.Fragment key={`int-${i}`}>
          <ArrowWithLabel label={translateAtom(puzzle.chain[i], lang)} />
          <Avatar name={ch.name} gender={ch.gender} />
        </React.Fragment>
      ))}
      <ArrowWithLabel label={translateAtom(puzzle.chain[puzzle.chain.length - 1], lang)} />
      <Avatar name={puzzle.egoName} gender={puzzle.egoGender} isEgo />
    </div>
  )
}

// #kinship-quiz-cutover-route — subtle inline CTA visible during
// gameplay. Small font, muted color, italic — designed to be
// noticeable without competing for attention with active puzzle
// interaction. Anchored to the bottom of PuzzleCard so it doesn't
// shift between answered / unanswered states.
function InlineSignupCta({ lang }: { lang: KinshipLang }): React.JSX.Element {
  const text = lang === 'en'
    ? 'Want to see YOUR family in Telugu kinship terms? Sign up →'
    : 'మీ నిజమైన కుటుంబాన్ని తెలుగు బంధుత్వ పదాల్లో చూడాలా? సైన్ అప్ చేయండి →'
  return (
    <div style={{
      marginTop: 16, paddingTop: 12,
      borderTop: '1px dashed #E2E8F0',
      textAlign: 'center',
    }}>
      <a
        href="/signup?ref=quiz-inline"
        style={{
          color: '#94A3B8', fontSize: 12, fontStyle: 'italic',
          textDecoration: 'none', lineHeight: 1.4,
        }}
      >
        {text}
      </a>
    </div>
  )
}

// #kinship-quiz-question-direction — pure helpers, exported for
// unit-testing without needing React Testing Library.
//
// Question direction: ego is always FIRST. The chain semantics
// place ego at the root and walk forward through the atoms to the
// alter being asked about. "What is alter to ego?" — read in
// Telugu as "egoNameకు alterName ఏమవుతారు?". The prior version
// inverted the order and was effectively asking "what is ego to
// alter?" — semantically the inverse of what kinshipLabels
// computes for the chain.
export function buildQuestionText(egoName: string, alterName: string, lang: KinshipLang): string {
  if (lang === 'en') return `What is ${alterName} to ${egoName}?`
  return `${egoName}కు ${alterName} ఏమవుతారు?`
}

// Per-hop atom label. Telugu form is the descriptive noun
// (తండ్రి/తల్లి/కొడుకు/…); English is the lowercased atom name.
// Used by ChainVisualization for the arrow labels between
// adjacent avatars.
//
// #quiz-l7-perspective-inversion Bug-3 — Wife/Husband added; L7
// pool entries use these atoms (e.g. [Husband], [Sister, Husband],
// [Son, Wife]) and the fallback path in `translateAtom` was leaking
// the English atom name (e.g. "husband") into the Telugu UI.
export const ATOM_TELUGU: Record<string, string> = {
  Father: 'తండ్రి', Mother: 'తల్లి',
  Son: 'కొడుకు',   Daughter: 'కూతురు',
  Brother: 'సోదరుడు', Sister: 'సోదరి',
  Husband: 'భర్త', Wife: 'భార్య',
}

export function translateAtom(atom: string, lang: KinshipLang): string {
  if (lang === 'en') return atom.toLowerCase()
  return ATOM_TELUGU[atom] ?? atom.toLowerCase()
}

function QuestionLine({ egoName, characters, lang }: {
  egoName: string
  characters: PuzzleCharacter[]
  lang: KinshipLang
}): React.JSX.Element {
  const last = characters[characters.length - 1]
  return (
    <p style={{ color: NAVY, fontSize: 16, fontWeight: 600, textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
      {buildQuestionText(egoName, last.name, lang)}
    </p>
  )
}

function ChainVisualization({ egoName, egoGender, characters, lang, visualSteps }: {
  egoName: string
  egoGender: CharacterGender
  characters: PuzzleCharacter[]
  lang: KinshipLang
  // #kinship-quiz-level-6-named-terms — when set, render one arrow
  // per visualStep (named-term + direct-atom for L6) instead of one
  // arrow per atom. Each step's characterIndex points at the
  // character the arrow lands on; intermediate characters are
  // hidden from the visualisation but participated in the engine
  // resolution.
  visualSteps?: import('../lib/quizPuzzle').VisualStep[]
}): React.JSX.Element {
  if (visualSteps && visualSteps.length > 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 20 }}>
        <Avatar name={egoName} gender={egoGender} isEgo />
        {visualSteps.map((step, i) => {
          const ch = characters[step.characterIndex]
          return (
            <React.Fragment key={i}>
              <ArrowWithLabel label={step.label} />
              <Avatar name={ch.name} gender={ch.gender} />
            </React.Fragment>
          )
        })}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 20 }}>
      <Avatar name={egoName} gender={egoGender} isEgo />
      {characters.map((ch, i) => (
        <React.Fragment key={i}>
          <ArrowWithLabel label={translateAtom(ch.atom, lang)} />
          <Avatar name={ch.name} gender={ch.gender} />
        </React.Fragment>
      ))}
    </div>
  )
}

// Avatar — name renders INSIDE the circle (#kinship-quiz-question-
// direction); no secondary label below. Gold + navy border = ego;
// gendered solid fill = others.
//
// #kinship-quiz-name-length: the previous .slice(0, 6) truncation
// was removed. The pool is curated to ≤6-char names so truncation
// is no longer needed — and silent truncation was the root cause of
// the "Falguni → Falgun" gender inversion. A defensive guard logs
// a warning if a >6-char name ever slips through (pool corruption,
// future regression); the avatar renders the full name in that
// case rather than silently truncating to a gender-ambiguous form.
function Avatar({ name, gender, isEgo }: {
  name: string
  gender: CharacterGender
  isEgo?: boolean
}): React.JSX.Element {
  const bg = isEgo ? GOLD : (gender === 'male' ? '#7986CB' : '#F48FB1')
  const fg = isEgo ? NAVY : '#FFF'
  if (name.length > 6) {
    console.warn(
      `Avatar: name "${name}" (${name.length} chars) exceeds the 6-char ` +
      `display cap — quizNames pool should enforce this; check the source`,
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
      <div style={{
        width: 56, height: 56, borderRadius: 28, background: bg, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: name.length > 4 ? 11 : 13, fontWeight: 700,
        border: isEgo ? `2px solid ${NAVY}` : 'none',
        padding: '0 4px',
      }}>
        {name}
      </div>
    </div>
  )
}

function ArrowWithLabel({ label }: { label: string }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
      <span style={{ color: '#9CA3AF', fontSize: 18, lineHeight: 1 }}>→</span>
      <span style={{ fontSize: 10, color: '#6B7280', marginTop: 2, fontStyle: 'italic' }}>{label}</span>
    </div>
  )
}

function OptionsGrid({ options, picked, onPick }: {
  options: AnswerOption[]
  picked: AnswerOption | null
  onPick: (o: AnswerOption) => void
}): React.JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
      {options.map((opt, i) => {
        const isPicked  = picked?.chainKey === opt.chainKey
        const showRight = picked && opt.isCorrect
        const showWrong = isPicked && !opt.isCorrect
        const bg = showRight ? '#E8F5E9' : showWrong ? '#FFEBEE' : '#FFF'
        const border = showRight ? GREEN : showWrong ? RED : '#E2E8F0'
        return (
          <button key={i} onClick={() => onPick(opt)} disabled={!!picked}
            style={{
              background: bg, border: `2px solid ${border}`, borderRadius: 10,
              padding: '12px 8px', fontSize: 15, fontWeight: 600,
              color: NAVY, cursor: picked ? 'default' : 'pointer',
              transition: 'all 0.12s',
            }}>
            {opt.term}
          </button>
        )
      })}
    </div>
  )
}

function Feedback({ picked, correct, onAdvance, onBack, canBack }: {
  picked: AnswerOption
  correct: string
  onAdvance: () => void
  onBack: () => void
  canBack: boolean
}): React.JSX.Element {
  const right = picked.isCorrect
  return (
    <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 16, marginTop: 8, textAlign: 'center' }}>
      <p style={{ color: right ? GREEN : RED, fontWeight: 700, fontSize: 16, margin: '0 0 8px' }}>
        {right ? 'సరైనది!' : `కాదు — సరైన సమాధానం: ${correct}`}
      </p>
      {/* #kinship-quiz-defer-l6-and-back-nav — Back/Next sibling
          row. Back stays in the DOM even when disabled (canBack
          === false on puzzleIndex=0) so the row layout doesn't
          shift between the first puzzle and subsequent ones. */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
        <button
          onClick={onBack}
          disabled={!canBack}
          style={canBack ? secondaryButtonStyle : disabledButtonStyle}
          aria-label="మునుపటి"
        >
          ← మునుపటి
        </button>
        <button onClick={onAdvance} style={primaryButtonStyle}>తదుపరి →</button>
      </div>
    </div>
  )
}

function LevelCompleteCard({ level, rightSoFar, outcome, canAdvance, onReplay, onAdvance, lang }: {
  level: number
  rightSoFar: number
  outcome: LevelOutcome
  canAdvance: boolean
  onReplay: () => void
  onAdvance: () => void
  lang: KinshipLang
}): React.JSX.Element {
  const passed = outcome === 'passed'
  return (
    <>
      <div style={cardStyle}>
        <p style={{ color: NAVY, fontSize: 18, fontWeight: 700, textAlign: 'center', margin: '0 0 12px' }}>
          స్థాయి {level} {passed ? 'పూర్తయింది!' : 'తిరిగి ప్రయత్నించండి'}
        </p>
        <p style={{ color: '#666', fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
          {rightSoFar} / {PUZZLES_PER_LEVEL} సరైనది
          {passed ? ' — ' + PASS_THRESHOLD + '/10 దాటారు' : ' — ' + PASS_THRESHOLD + '/10 అవసరం'}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onReplay} style={secondaryButtonStyle}>మళ్ళీ ఆడు</button>
          {passed && canAdvance && (
            <button onClick={onAdvance} style={primaryButtonStyle}>తదుపరి స్థాయి →</button>
          )}
        </div>
      </div>
      <LevelCompleteSignupCta lang={lang} />
    </>
  )
}

// #kinship-quiz-cutover-route — richer signup CTA shown after a
// level completes (passed or failed). Encouraging tone, visually
// distinct from the puzzle UI, primary CTA button. Sibling of
// LevelCompleteCard so it's spatially anchored to the same moment.
function LevelCompleteSignupCta({ lang }: { lang: KinshipLang }): React.JSX.Element {
  const heading = lang === 'en'
    ? "Nice work — you're getting the hang of this."
    : 'బాగుంది — మీకు బంధుత్వ పదాలు బాగా అర్థమవుతున్నాయి.'
  const body = lang === 'en'
    ? 'Build YOUR Vamsa Vriksham (family tree) on Jnati →'
    : 'మీ వంశ వృక్షాన్ని Jnatiలో సృష్టించండి →'
  return (
    <div style={{
      ...cardStyle,
      marginTop: 16,
      background: NAVY,
      color: '#FFF',
      borderColor: GOLD,
      textAlign: 'center',
    }}>
      <p style={{ color: GOLD, fontWeight: 700, fontSize: 14, margin: '0 0 6px' }}>
        {heading}
      </p>
      <p style={{ color: '#FFF', fontSize: 13, margin: '0 0 16px', opacity: 0.9 }}>
        {body}
      </p>
      <a
        href="/signup?ref=quiz-level-complete"
        style={{
          display: 'inline-block',
          background: GOLD, color: NAVY, padding: '10px 24px',
          borderRadius: 8, fontSize: 14, fontWeight: 700,
          textDecoration: 'none',
        }}
      >
        {lang === 'en' ? 'Create account →' : 'ఖాతా సృష్టించండి →'}
      </a>
    </div>
  )
}

function ComingSoonCard({ level, onBack }: { level: number; onBack: () => void }): React.JSX.Element {
  // Reachable only when the player has cleared L6 and tapped Next.
  // All 6 quiz levels are supported as of commit 4; this is the
  // "no more levels" terminal state.
  return (
    <div style={cardStyle}>
      <p style={{ color: NAVY, fontSize: 16, fontWeight: 700, textAlign: 'center', margin: '0 0 8px' }}>
        స్థాయి {level} — త్వరలో
      </p>
      <p style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 16, lineHeight: 1.5 }}>
        మీరు అన్ని ప్రస్తుత స్థాయిలు పూర్తి చేశారు.<br />
        Future levels will land in upcoming dispatches.
      </p>
      <div style={{ textAlign: 'center' }}>
        <button onClick={onBack} style={secondaryButtonStyle}>మొదలు నుండి</button>
      </div>
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────

function trimWindow<T>(set: Set<T>, max: number): Set<T> {
  if (set.size <= max) return set
  const arr = Array.from(set)
  return new Set(arr.slice(arr.length - max))
}

// ── styles ───────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: '#FFF', borderRadius: 12, padding: 24,
  border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
}

const chipStyle: React.CSSProperties = {
  background: '#F1F5F9', color: NAVY, padding: '4px 12px',
  borderRadius: 999, fontWeight: 600,
}

// #kinship-quiz-level-selector — match the existing pill rhythm
// but each level is its own button. Active = filled navy (matches
// the legacy active-chip look). Inactive = white outlined, same
// navy text — same affordance as primary/secondary buttons
// elsewhere in the component.
const levelButtonActiveStyle: React.CSSProperties = {
  background: NAVY, color: '#FFF', padding: '6px 14px',
  border: `1.5px solid ${NAVY}`, borderRadius: 999,
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
  minWidth: 56, transition: 'all 0.12s',
}

const levelButtonInactiveStyle: React.CSSProperties = {
  background: '#FFF', color: NAVY, padding: '6px 14px',
  border: `1.5px solid ${NAVY}`, borderRadius: 999,
  fontWeight: 600, fontSize: 13, cursor: 'pointer',
  minWidth: 56, transition: 'all 0.12s',
}

const primaryButtonStyle: React.CSSProperties = {
  background: NAVY, color: '#FFF', padding: '10px 24px', border: 'none',
  borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  background: '#FFF', color: NAVY, padding: '10px 24px',
  border: `1.5px solid ${NAVY}`, borderRadius: 8,
  fontSize: 14, fontWeight: 700, cursor: 'pointer',
}

// #kinship-quiz-defer-l6-and-back-nav — disabled Back button on
// the first puzzle of a level. Same footprint as secondaryButton
// so the Back/Next row doesn't shift, just muted colors.
const disabledButtonStyle: React.CSSProperties = {
  background: '#F5F7FA', color: '#9CA3AF', padding: '10px 24px',
  border: '1.5px solid #E2E8F0', borderRadius: 8,
  fontSize: 14, fontWeight: 700, cursor: 'not-allowed',
}
